import { useState, useRef, useEffect  } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import HighchartsExporting from 'highcharts/modules/exporting'
import highchartsAccessibility from 'highcharts/modules/accessibility'
import CorrelationLegend from '../CorrelationLegend'
import NoDataImage from '../../../../common/components/NoDataImage'
import useCloseOnOutsideClick from '../../../../common/hooks/useCloseOnOutsideClick'
import { useReactToPrint } from 'react-to-print'
import { toPng, toJpeg, toSvg } from 'html-to-image'

HighchartsExporting(Highcharts)
highchartsAccessibility(Highcharts)

Highcharts.setOptions({
  chart: {
    style: {
      fontFamily: '"Open Sans", sansSerif',
    },
  },
})

const ScatterChart = ({
  chartOptions,
  chartKey,
  noDataImageURL,
  indicatorCode,
  indicatorSpecificRegions,
  selectedOnlyShow,
}) => {
  const { series, xAxis } = chartOptions
  const noData =
    !Array.isArray(series) ||
    series?.length === 0 ||
    series?.every((item) => !item?.data || item?.data?.length === 0)
  const hasAggregateData = xAxis?.plotLines && xAxis?.plotLines.length >= 2 // checking length 2 since there is a default plotLine for 0

  const [showMenu, setShowMenu] = useState(false)
  const [isFullScreenMode, setIsFullScreenMode] = useState(false)
  const scatterChartDownloadMenuRef = useRef(null)

  useCloseOnOutsideClick(scatterChartDownloadMenuRef, () => setShowMenu(false))

  const excludeElements = (node) => {
    const exclusionClasses = ['download-exclude']
    return !exclusionClasses.some((classname) =>
      node.classList?.contains(classname)
    )
  }

  const exportAsImage = (fileName, type) => {
    const node = document.getElementById('scatter_chart_container')

    if (type === 'png') {
      toPng(node, { filter: excludeElements, backgroundColor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    } else if (type === 'jpeg') {
      toJpeg(node, { filter: excludeElements, backgroundColor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    } else if (type === 'svg') {
      toSvg(node, { filter: excludeElements, backgroundColor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      enterFullscreen()
    } else {
      exitFullscreen()
    }
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullScreenMode(false)
      }
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const enterFullscreen = () => {
    const elem = document.getElementById('scatter_chart_container')
    if (elem.requestFullscreen) {
      elem.requestFullscreen()
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen()
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen()
    }
    setIsFullScreenMode(true)
  }

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen()
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen()
    }
  }

  const handlePrint = useReactToPrint({
    content: () => document.getElementById('scatter_chart_container'),
  })
  return (
    <div id="scatter_chart_container">
      <div className="tui_comparison_indicator_container tui_chart_dropdown">
        {(!noData || hasAggregateData) && (
          <div
            className="button-container download-exclude"
            ref={scatterChartDownloadMenuRef}
          >
            <div
              className="download-icon"
              onClick={() => setShowMenu((prevState) => !prevState)}
              role="button"
              tabIndex="0"
              aria-haspopup="true"
              aria-expanded={showMenu ? 'true' : 'false'}
              id="scatterChartDownloadMenu"
            >
              <span className="lp lp-bars"></span>
            </div>
            <div
              className={`download-options download-exclude ${
                showMenu ? 'show' : 'hide'
              }`}
              aria-labelledby="scatterChartDownloadMenu"
            >
              <ul>
                <li
                  onClick={() => {
                    handleFullscreen()
                    setShowMenu(false)
                  }}
                >
                  {isFullScreenMode
                    ? 'Exit from full screen'
                    : 'View in full screen'}
                </li>
                <li
                  onClick={() => {
                    const fileName = `${indicatorCode}_scatter.png`
                    exportAsImage(fileName, 'png')
                    setShowMenu(false)
                  }}
                >
                  Download PNG image
                </li>
                <li
                  onClick={() => {
                    const fileName = `${indicatorCode}_scatter.jpg`
                    exportAsImage(fileName, 'jpeg')
                    setShowMenu(false)
                  }}
                >
                  Download JPEG image
                </li>
                <li
                  onClick={() => {
                    const fileName = `${indicatorCode}_scatter.svg`
                    exportAsImage(fileName, 'svg')
                    setShowMenu(false)
                  }}
                >
                  Download SVG
                </li>
                <li
                  onClick={() => {
                    handlePrint()
                    setShowMenu(false)
                  }}
                >
                  Print
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
      <div className="tui__chart_content">
        {!noData || hasAggregateData ? (
          <>
            <figure className="highcharts-figure">
              <HighchartsReact
                key={`highcharts-${chartKey}`}
                highcharts={Highcharts}
                options={chartOptions}
              />
            </figure>
            <CorrelationLegend
              indicatorSpecificRegions={indicatorSpecificRegions}
              selectedOnlyShow={selectedOnlyShow}
            />
          </>
        ) : (
          <NoDataImage imageURL={noDataImageURL} height="345px" width="100%" />
        )}
      </div>
    </div>
  )
}
export default ScatterChart
